import { useState, useCallback, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";

interface ParsedRow {
  satisNo: string;
  tarih: string;
  sube: string;
  satisPersoneli: string;
  toplamTutar: number;
  odemeTipi: string;
  odemeDurumu: string;
  odemePersoneli: string;
  odaNo: string;
  musteriAdi: string;
}

interface ImportResult {
  total: number;
  imported: number;
  skipped: number;
  errors: string[];
}

interface DealerOption {
  id: string;
  dealer_name: string;
}

export default function RestaurantSalesImportPage() {
  const { user } = useAuth();
  const dealerId = user?.dealerId;

  const [file, setFile] = useState<File | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [previewRows, setPreviewRows] = useState<ParsedRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const [selectedDealerId, setSelectedDealerId] = useState<string>(dealerId || "");
  const [dealers, setDealers] = useState<DealerOption[]>([]);
  const [staffMap, setStaffMap] = useState<Record<string, string>>();
  const [step, setStep] = useState<"upload" | "preview" | "importing" | "done">("upload");

  // Load dealers on mount
  useEffect(() => {
    const fetchDealers = async () => {
      try {
        const supabase = getSupabase();
        const { data, error: err } = await supabase
          .from("dealers")
          .select("id, dealer_name")
          .eq("has_restaurant", true)
          .order("dealer_name");
        if (err) throw err;
        const loaded = data || [];
        setDealers(loaded);
        // Pre-select first dealer for admin users if none selected
        if (loaded.length > 0 && !selectedDealerId) {
          setSelectedDealerId(loaded[0].id);
        }
      } catch (e: any) {
        setError("Bayiler yüklenirken hata: " + (e.message || e));
      }
    };
    fetchDealers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paymentMethodMap: Record<string, string> = {
    // Nakit
    "Nakit": "nakit",
    "nakit": "nakit",
    "NAKIT": "nakit",
    // POS / Kredi Kartı
    "Kredi Kartı": "pos",
    "Kredi Kart": "pos",
    "POS": "pos",
    "Pos": "pos",
    "pos": "pos",
    "KREDİ KARTI": "pos",
    "KREDI KARTI": "pos",
    "KREDI KART": "pos",
    // Oda Hesabı / Cari
    "Oda Hesabı": "cari",
    "Oda Hesabi": "cari",
    "ODA HESABI": "cari",
    "Cari": "cari",
    "cari": "cari",
    "CARI": "cari",
    // Havale / EFT / İban
    "Havale": "iban",
    "Havale/EFT": "iban",
    "EFT": "iban",
    "İban": "iban",
    "IBAN": "iban",
    "iban": "iban",
    // Kısmi ödeme — varsayılan nakit
    "Kısmi Ödeme": "nakit",
    "Kismi Odeme": "nakit",
    "KISMİ ÖDEME": "nakit",
    "Peşin": "nakit",
    "Pesin": "nakit",
    "Nakit Ödeme": "nakit",
  };

  const paymentStatusMap: Record<string, string> = {
    "Ödendi": "completed",
    "Beklemede": "pending",
  };

  const parseAmount = (raw: string): number => {
    if (!raw) return 0;
    let cleaned = raw.replace(/[₺\s]/g, "").trim();
    if (!cleaned) return 0;

    // Detect format: 1.250,00 (Turkish) vs 1,250.00 (US)
    const hasCommaDecimal = /,\d{2}$/.test(cleaned);
    const hasDotDecimal = /\.\d{2}$/.test(cleaned);

    if (hasCommaDecimal) {
      // Turkish: dots are thousand separators, comma is decimal
      cleaned = cleaned.replace(/\./g, "").replace(",", ".");
    } else if (hasDotDecimal) {
      // US format: comma is thousand separator, dot is decimal
      cleaned = cleaned.replace(/,/g, "");
    } else {
      // No clear decimal - might be like "1250" or "1.250" (integer)
      // If there are dots and commas mixed, prefer comma as decimal
      if (cleaned.includes(",") && cleaned.includes(".")) {
        const lastComma = cleaned.lastIndexOf(",");
        const lastDot = cleaned.lastIndexOf(".");
        if (lastComma > lastDot) {
          cleaned = cleaned.replace(/\./g, "").replace(",", ".");
        } else {
          cleaned = cleaned.replace(/,/g, "");
        }
      } else if (cleaned.includes(",")) {
        // Could be decimal or thousand separator - check position
        const parts = cleaned.split(",");
        if (parts.length === 2 && parts[1].length <= 2) {
          cleaned = parts[0].replace(/\./g, "") + "." + parts[1];
        } else {
          cleaned = cleaned.replace(/,/g, "");
        }
      } else if (cleaned.includes(".")) {
        const parts = cleaned.split(".");
        if (parts.length === 2 && parts[1].length <= 2) {
          // dot is decimal - cleaned is already correct
        } else {
          // dots are thousand separators
          cleaned = cleaned.replace(/\./g, "");
        }
      }
    }

    const val = parseFloat(cleaned);
    return isNaN(val) ? 0 : val;
  };

  const parseDate = (raw: string): string => {
    if (!raw) return new Date().toISOString();
    const trimmed = raw.trim();

    // Try to extract date and time parts
    let dateStr = trimmed;
    let timeStr = "";

    const spaceSplit = trimmed.split(" ");
    if (spaceSplit.length >= 2) {
      // Check if last part looks like time (contains :)
      const lastPart = spaceSplit[spaceSplit.length - 1];
      if (lastPart.includes(":")) {
        timeStr = lastPart;
        dateStr = spaceSplit.slice(0, spaceSplit.length - 1).join(" ");
      }
    }

    // Parse date - support both dd.mm.yyyy and dd/mm/yyyy
    let day = 1, month = 1, year = new Date().getFullYear();

    const dotParts = dateStr.split(".");
    const slashParts = dateStr.split("/");

    if (dotParts.length === 3) {
      day = parseInt(dotParts[0], 10) || 1;
      month = parseInt(dotParts[1], 10) || 1;
      year = parseInt(dotParts[2], 10) || new Date().getFullYear();
    } else if (slashParts.length === 3) {
      day = parseInt(slashParts[0], 10) || 1;
      month = parseInt(slashParts[1], 10) || 1;
      year = parseInt(slashParts[2], 10) || new Date().getFullYear();
    } else {
      // Try to parse as-is with native Date
      const fallback = new Date(dateStr);
      if (!isNaN(fallback.getTime())) {
        return fallback.toISOString();
      }
    }

    // Fix 2-digit years
    if (year < 100) {
      year += year < 50 ? 2000 : 1900;
    }

    // Parse time
    let hour = 0, minute = 0;
    if (timeStr) {
      const timeParts = timeStr.split(":");
      hour = parseInt(timeParts[0], 10) || 0;
      minute = parseInt(timeParts[1], 10) || 0;
    }

    const dt = new Date(year, month - 1, day, hour, minute);
    return dt.toISOString();
  };

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
        continue;
      }
      if (char === ';' && !inQuotes) {
        result.push(current.trim());
        current = "";
        continue;
      }
      current += char;
    }
    result.push(current.trim());
    return result;
  };

  const loadDealers = useCallback(async () => {
    try {
      const supabase = getSupabase();
      const { data, error: err } = await supabase
        .from("dealers")
        .select("id, dealer_name")
        .eq("has_restaurant", true)
        .order("dealer_name");
      if (err) throw err;
      const loaded = data || [];
      setDealers(loaded);
      if (loaded.length > 0 && !selectedDealerId) {
        setSelectedDealerId(loaded[0].id);
      }
      return loaded;
    } catch (e: any) {
      setError("Bayiler yüklenirken hata: " + (e.message || e));
      return [];
    }
  }, [selectedDealerId]);

  const loadStaffAndProduct = useCallback(async (targetDealerId: string): Promise<{ staffMap: Record<string, string>; productId: string | null }> => {
    if (!targetDealerId) {
      throw new Error("Bayi ID belirtilmemiş. Lütfen bir bayi seçin.");
    }
    const supabase = getSupabase();
    const { data: staffData } = await supabase
      .from("staff")
      .select("id, full_name")
      .eq("dealer_id", targetDealerId);

    const map: Record<string, string> = {};
    (staffData || []).forEach((s: any) => {
      map[s.full_name.trim()] = s.id;
    });

    // No phantom product needed — items will be inserted with null product_id
    // and a meaningful description built from the row data
    return { staffMap: map, productId: null };
  }, []);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;

    // Ensure dealer is selected before parsing
    let effectiveDealerId = selectedDealerId;
    if (!effectiveDealerId) {
      const loaded = await loadDealers();
      if (loaded.length > 0) {
        effectiveDealerId = loaded[0].id;
        setSelectedDealerId(effectiveDealerId);
      }
    }
    if (!effectiveDealerId) {
      setError("Lütfen önce bir bayi seçin.");
      return;
    }

    setFile(f);
    setError("");
    setResult(null);
    setStep("upload");

    const text = await f.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) {
      setError("CSV dosyasında veri bulunamadı. En az başlık satırı ve 1 veri satırı gerekli.");
      return;
    }

    const header = parseCSVLine(lines[0]);
    const expectedCols = ["Satış No", "Tarih", "Şube", "Satış Personeli", "Toplam Tutar", "Ödeme Tipi", "Ödeme Durumu", "Ödeme Personeli", "Oda No", "Müşteri Adı"];
    const hasExpected = expectedCols.every((col) =>
      header.some((h) => h.toLowerCase().includes(col.toLowerCase()))
    );
    if (!hasExpected) {
      setError("CSV formatı tanınmadı. Beklenen sütunlar: Satış No, Tarih, Şube, Satış Personeli, Toplam Tutar, Ödeme Tipi, Ödeme Durumu, Ödeme Personeli, Oda No, Müşteri Adı");
      return;
    }

    const rows: ParsedRow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = parseCSVLine(lines[i]);
      if (cols.length < 6) continue;

      const satisNo = cols[0]?.replace(/"/g, "").trim() || "";
      const tarihRaw = cols[1]?.replace(/"/g, "").trim() || "";
      const sube = cols[2]?.replace(/"/g, "").trim() || "";
      const satisPersoneli = cols[3]?.replace(/"/g, "").trim() || "";
      const tutarRaw = cols[4]?.replace(/"/g, "").trim() || "";
      const odemeTipi = cols[5]?.replace(/"/g, "").trim() || "";
      const odemeDurumu = cols[6]?.replace(/"/g, "").trim() || "Ödendi";
      const odemePersoneli = cols[7]?.replace(/"/g, "").trim() || "";
      const odaNo = cols[8]?.replace(/"/g, "").trim() || "";
      const musteriAdi = cols[9]?.replace(/"/g, "").trim() || "";

      if (!satisNo || !tarihRaw) continue;

      rows.push({
        satisNo,
        tarih: parseDate(tarihRaw),
        sube,
        satisPersoneli,
        toplamTutar: parseAmount(tutarRaw),
        odemeTipi,
        odemeDurumu,
        odemePersoneli,
        odaNo,
        musteriAdi,
      });
    }

    if (rows.length === 0) {
      setError("CSV dosyasında geçerli veri satırı bulunamadı.");
      return;
    }

    setParsedRows(rows);
    setPreviewRows(rows.slice(0, 20));
    setStep("preview");

    try {
      const { staffMap: map } = await loadStaffAndProduct(effectiveDealerId);
      setStaffMap(map);
    } catch (e: any) {
      setError("Personel bilgisi yüklenirken hata: " + (e.message || "Bilinmeyen hata"));
    }
  };

  const importData = async () => {
    if (!selectedDealerId) {
      setError("Lütfen bir bayi seçin.");
      return;
    }
    if (parsedRows.length === 0) {
      setError("İçe aktarılacak veri bulunamadı.");
      return;
    }

    setImporting(true);
    setStep("importing");
    setProgress(0);
    setError("");
    setResult(null);

    try {
      const supabase = getSupabase();

      const { staffMap: localStaffMap } = await loadStaffAndProduct(selectedDealerId);

      // Find matching current_dealer record for this dealer
      let currentDealerId: string | null = null;
      const { data: currentDealerData } = await supabase
        .from("current_dealers")
        .select("id")
        .eq("dealer_id", selectedDealerId)
        .maybeSingle();
      if (currentDealerData) {
        currentDealerId = currentDealerData.id;
      }

      // Check existing sales in chunks to avoid URL length limits
      const allSaleNumbers = parsedRows.map((r) => r.satisNo);
      const existingNumbers = new Set<string>();
      const CHUNK = 200;
      for (let i = 0; i < allSaleNumbers.length; i += CHUNK) {
        const chunk = allSaleNumbers.slice(i, i + CHUNK);
        const { data: existingChunk, error: existErr } = await supabase
          .from("sales")
          .select("sale_number")
          .eq("dealer_id", selectedDealerId)
          .in("sale_number", chunk);
        if (existErr) {
          throw new Error("Mevcut satışlar kontrol edilirken hata: " + existErr.message);
        }
        (existingChunk || []).forEach((s: any) => existingNumbers.add(s.sale_number));
      }

      // Create missing staff
      const staffToCreate: string[] = [];
      parsedRows.forEach((row) => {
        const sName = row.satisPersoneli.trim();
        const oName = row.odemePersoneli.trim();
        if (sName && sName !== "-" && !localStaffMap[sName]) staffToCreate.push(sName);
        if (oName && oName !== "-" && !localStaffMap[oName]) staffToCreate.push(oName);
      });

      const uniqueStaff = [...new Set(staffToCreate)];
      for (const name of uniqueStaff) {
        const { data, error: staffErr } = await supabase
          .from("staff")
          .insert({ dealer_id: selectedDealerId, full_name: name, role: "Garson", is_active: true })
          .select("id, full_name")
          .single();
        if (staffErr) {
          throw new Error(`Personel oluşturulamadı (${name}): ${staffErr.message}`);
        }
        if (data) {
          localStaffMap[data.full_name] = data.id;
        }
      }

      let imported = 0;
      let skipped = 0;
      const errors: string[] = [];
      const BATCH_SIZE = 25;

      for (let i = 0; i < parsedRows.length; i += BATCH_SIZE) {
        const batch = parsedRows.slice(i, i + BATCH_SIZE);
        const salesToInsert: any[] = [];
        const itemsToInsert: any[] = [];
        const saleIdMap: Record<string, string> = {};

        for (const row of batch) {
          if (existingNumbers.has(row.satisNo)) {
            skipped++;
            continue;
          }

          const staffId = localStaffMap[row.satisPersoneli.trim()] || null;
          const collectedById = row.odemePersoneli.trim() && row.odemePersoneli.trim() !== "-"
            ? localStaffMap[row.odemePersoneli.trim()] || null
            : null;

          const rawPaymentMethod = row.odemeTipi.trim();
          const pMethod = paymentMethodMap[rawPaymentMethod] || paymentMethodMap[rawPaymentMethod.toLowerCase()] || paymentMethodMap[rawPaymentMethod.toUpperCase()] || "nakit";
          const pStatus = paymentStatusMap[row.odemeDurumu.trim()] || "completed";

          const saleId = crypto.randomUUID();
          saleIdMap[row.satisNo] = saleId;

          salesToInsert.push({
            id: saleId,
            dealer_id: selectedDealerId,
            current_dealer_id: currentDealerId,
            sale_number: row.satisNo,
            payment_method: pMethod,
            total_amount: row.toplamTutar,
            created_at: row.tarih,
            room_number: row.odaNo || null,
            customer_name: row.musteriAdi || null,
            sale_type: "restaurant",
            staff_id: staffId,
            collected_by_staff_id: collectedById,
            payment_status: pStatus,
            collected_at: pStatus === "completed" && collectedById ? row.tarih : null,
          });

          itemsToInsert.push({
            id: crypto.randomUUID(),
            sale_id: saleId,
            product_id: null,
            quantity: 1,
            unit_price: row.toplamTutar,
            purchase_price: 0,
            description: [row.musteriAdi, row.odaNo ? `Oda ${row.odaNo}` : ""].filter(Boolean).join(" - ") || "İçe Aktarılan Satış",
          });
        }

        if (salesToInsert.length > 0) {
          const { error: salesErr } = await supabase.from("sales").insert(salesToInsert);
          if (salesErr) {
            errors.push(`Satış grubu hatası (${i + 1}-${Math.min(i + BATCH_SIZE, parsedRows.length)}): ${salesErr.message}`);
            // Try to clean up any partial inserts if possible
            const insertedIds = salesToInsert.map((s) => s.id);
            await supabase.from("sales").delete().in("id", insertedIds).eq("dealer_id", selectedDealerId);
          } else {
            imported += salesToInsert.length;
            const { error: itemsErr } = await supabase.from("sale_items").insert(itemsToInsert);
            if (itemsErr) {
              errors.push(`Kalem grubu hatası (${i + 1}-${Math.min(i + BATCH_SIZE, parsedRows.length)}): ${itemsErr.message}`);
              // If items failed but sales succeeded, try to delete the sales to maintain consistency
              const insertedIds = salesToInsert.map((s) => s.id);
              await supabase.from("sales").delete().in("id", insertedIds).eq("dealer_id", selectedDealerId);
              imported -= salesToInsert.length;
            }
          }
        }

        setProgress(Math.min(100, Math.round(((i + batch.length) / parsedRows.length) * 100)));
      }

      setResult({ total: parsedRows.length, imported, skipped, errors });
      setStep("done");
    } catch (e: any) {
      setError(e.message || "İçe aktarma sırasında beklenmedik bir hata oluştu.");
      setStep("preview");
    } finally {
      setImporting(false);
    }
  };

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(val);

  const formatDateShort = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const unknownStaffCount = useMemo(() => {
    if (!staffMap || Object.keys(staffMap).length === 0) return 0;
    const unknown = new Set<string>();
    parsedRows.forEach((r) => {
      if (r.satisPersoneli && r.satisPersoneli !== "-" && !staffMap[r.satisPersoneli]) unknown.add(r.satisPersoneli);
      if (r.odemePersoneli && r.odemePersoneli !== "-" && !staffMap[r.odemePersoneli]) unknown.add(r.odemePersoneli);
    });
    return unknown.size;
  }, [parsedRows, staffMap]);

  const paymentTypeSummary = useMemo(() => {
    const counts: Record<string, { count: number; total: number }> = {};
    parsedRows.forEach((r) => {
      const key = r.odemeTipi || "Diğer";
      if (!counts[key]) counts[key] = { count: 0, total: 0 };
      counts[key].count++;
      counts[key].total += r.toplamTutar;
    });
    return counts;
  }, [parsedRows]);

  const statusSummary = useMemo(() => {
    const counts: Record<string, number> = {};
    parsedRows.forEach((r) => {
      const key = r.odemeDurumu || "Diğer";
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [parsedRows]);

  if (!user?.hasRestaurantReports) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-20 h-20 rounded-full bg-accent-100 flex items-center justify-center mb-4">
          <i className="ri-lock-line text-3xl text-accent-500"></i>
        </div>
        <h4 className="text-lg font-semibold text-foreground-950 mb-1">Erişim Kısıtlandı</h4>
        <p className="text-sm text-foreground-500 mb-4 text-center max-w-sm">
          Bu sayfaya erişmek için bayi yöneticinizden restoran modülü yetkisi talep edin.
        </p>
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          Dashboard&apos;a Dön
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
              <i className="ri-file-upload-line text-primary-600"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950">Restoran Satış CSV İçe Aktar</h1>
          </div>
          <p className="text-sm text-foreground-500 mt-0.5">
            CSV formatındaki restoran satış raporlarını sisteme yükleyin
          </p>
        </div>
        <Link
          to="/reports/restaurant"
          className="inline-flex items-center gap-2 px-4 py-2 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          Satış Raporlarına Dön
        </Link>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {[
          { key: "upload", label: "Dosya Seç", icon: "ri-upload-cloud-2-line" },
          { key: "preview", label: "Önizleme", icon: "ri-eye-line" },
          { key: "done", label: "Sonuç", icon: "ri-check-line" },
        ].map((s, idx) => {
          const isActive = step === s.key || (s.key === "preview" && step === "importing");
          const isDone = step === "done" && idx < 2;
          return (
            <div key={s.key} className="flex items-center gap-2">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                isActive
                  ? "bg-primary-500 text-background-50"
                  : isDone
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-background-100 text-foreground-400"
              }`}>
                <i className={`${isDone ? "ri-checkbox-circle-fill" : s.icon}`}></i>
                {s.label}
              </div>
              {idx < 2 && <i className="ri-arrow-right-s-line text-foreground-300 text-xs"></i>}
            </div>
          );
        })}
      </div>

      {error && (
        <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button
            onClick={() => setError("")}
            className="ml-auto text-accent-700 font-medium cursor-pointer whitespace-nowrap"
          >
            Kapat
          </button>
        </div>
      )}

      {/* Upload Section */}
      {(step === "upload" || step === "preview") && (
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 space-y-4">
          {/* Dealer Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-2">
              <i className="ri-building-line mr-1"></i>
              Bayi Seçimi
            </label>
            <select
              value={selectedDealerId}
              onChange={(e) => setSelectedDealerId(e.target.value)}
              className="w-full md:w-80 px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="">Bayi seçin...</option>
              {dealers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.dealer_name}
                </option>
              ))}
            </select>
            {dealers.length === 0 && (
              <p className="text-xs text-accent-500 mt-1">
                <i className="ri-error-warning-line mr-1"></i>
                Restoran modülü aktif bayi bulunamadı.
              </p>
            )}
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-2">
              <i className="ri-file-text-line mr-1"></i>
              CSV Dosyası
            </label>
            <div className="relative">
              <input
                type="file"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="block w-full text-sm text-foreground-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-500 file:text-background-50 hover:file:bg-primary-600 file:cursor-pointer cursor-pointer"
              />
            </div>
            <p className="text-xs text-foreground-400 mt-2">
              Beklenen format: <code className="bg-background-100 px-1 py-0.5 rounded text-foreground-600">Satış No;Tarih;Şube;Satış Personeli;Toplam Tutar;Ödeme Tipi;Ödeme Durumu;Ödeme Personeli;Oda No;Müşteri Adı</code>
            </p>
          </div>
        </div>
      )}

      {/* Preview Section */}
      {step === "preview" && parsedRows.length > 0 && (
        <div className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
              <p className="text-xs text-foreground-400 mb-1">Toplam Satır</p>
              <p className="text-lg font-bold text-foreground-950">{parsedRows.length}</p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
              <p className="text-xs text-foreground-400 mb-1">Toplam Tutar</p>
              <p className="text-lg font-bold text-foreground-950">
                {formatCurrency(parsedRows.reduce((s, r) => s + r.toplamTutar, 0))}
              </p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
              <p className="text-xs text-foreground-400 mb-1">Tarih Aralığı</p>
              <p className="text-sm font-bold text-foreground-950">
                {formatDateShort(parsedRows[parsedRows.length - 1]?.tarih || "")} -
                {formatDateShort(parsedRows[0]?.tarih || "")}
              </p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
              <p className="text-xs text-foreground-400 mb-1">Yeni Personel</p>
              <p className="text-lg font-bold text-foreground-950">
                {unknownStaffCount > 0 ? (
                  <span className="text-accent-500">{unknownStaffCount} kişi</span>
                ) : (
                  <span className="text-emerald-600">0</span>
                )}
              </p>
            </div>
          </div>

          {/* Payment type summary */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <p className="text-sm font-medium text-foreground-700 mb-3">
              <i className="ri-pie-chart-line mr-1"></i>
              Ödeme Türü Dağılımı
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(paymentTypeSummary).map(([key, val]) => (
                <div key={key} className="px-3 py-1.5 bg-background-100 rounded-lg text-xs">
                  <span className="font-medium text-foreground-700">{key}</span>
                  <span className="text-foreground-400 mx-1">·</span>
                  <span className="text-foreground-900">{val.count} satış</span>
                  <span className="text-foreground-400 mx-1">·</span>
                  <span className="font-semibold text-foreground-900">{formatCurrency(val.total)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Status summary */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <p className="text-sm font-medium text-foreground-700 mb-3">
              <i className="ri-checkbox-circle-line mr-1"></i>
              Ödeme Durumu
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(statusSummary).map(([key, val]) => (
                <div key={key} className="px-3 py-1.5 bg-background-100 rounded-lg text-xs">
                  <span className="font-medium text-foreground-700">{key}</span>
                  <span className="text-foreground-400 mx-1">·</span>
                  <span className="text-foreground-900">{val} satış</span>
                </div>
              ))}
            </div>
          </div>

          {/* Preview table */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200/70 flex items-center justify-between">
              <p className="text-sm font-medium text-foreground-700">
                <i className="ri-table-line mr-1"></i>
                Önizleme (ilk 20 satır)
              </p>
              <p className="text-xs text-foreground-400">{parsedRows.length} satır toplam</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="bg-background-100 text-left">
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Satış No</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Tarih</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Satış Personeli</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Ödeme Türü</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Ödeme Durumu</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Ödeme Personeli</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Oda No</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Müşteri</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500 text-right">Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, idx) => (
                    <tr key={idx} className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors">
                      <td className="px-4 py-2.5 text-xs text-foreground-600 font-mono">{row.satisNo}</td>
                      <td className="px-4 py-2.5 text-xs text-foreground-600">{formatDateShort(row.tarih)}</td>
                      <td className="px-4 py-2.5 text-xs text-foreground-900">{row.satisPersoneli || "-"}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          row.odemeTipi === "Nakit"
                            ? "bg-primary-100 text-primary-700"
                            : row.odemeTipi === "Kredi Kartı"
                              ? "bg-accent-100 text-accent-700"
                              : "bg-secondary-100 text-secondary-700"
                        }`}>
                          {row.odemeTipi}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          row.odemeDurumu === "Ödendi"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}>
                          {row.odemeDurumu}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-foreground-900">{row.odemePersoneli || "-"}</td>
                      <td className="px-4 py-2.5 text-xs text-foreground-600">{row.odaNo || "-"}</td>
                      <td className="px-4 py-2.5 text-xs text-foreground-600">{row.musteriAdi || "-"}</td>
                      <td className="px-4 py-2.5 text-right text-xs font-semibold text-foreground-950">
                        {formatCurrency(row.toplamTutar)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Import button */}
          <div className="flex items-center justify-between">
            <div className="text-xs text-foreground-400">
              {unknownStaffCount > 0 && (
                <span className="flex items-center gap-1 text-accent-500">
                  <i className="ri-information-line"></i>
                  {unknownStaffCount} yeni personel otomatik oluşturulacak
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { setStep("upload"); setParsedRows([]); setPreviewRows([]); setFile(null); }}
                className="px-4 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line mr-1"></i>
                İptal
              </button>
              <button
                onClick={importData}
                disabled={importing || !selectedDealerId}
                className="px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {importing ? (
                  <span className="flex items-center gap-1.5">
                    <i className="ri-loader-4-line animate-spin"></i>
                    İşleniyor...
                  </span>
                ) : (
                  <>
                    <i className="ri-upload-cloud-2-line mr-1"></i>
                    {parsedRows.length} Satırı İçe Aktar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Importing Progress */}
      {step === "importing" && (
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-8 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-600"></i>
          </div>
          <p className="text-lg font-semibold text-foreground-950">İçe Aktarma Devam Ediyor...</p>
          <p className="text-sm text-foreground-500">Lütfen bekleyin, satışlar veritabanına yazılıyor.</p>
          <div className="max-w-md mx-auto">
            <div className="h-2 bg-background-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-xs text-foreground-400 mt-2">%{progress}</p>
          </div>
        </div>
      )}

      {/* Done / Result */}
      {step === "done" && result && (
        <div className="space-y-4">
          <div className="bg-emerald-50 border border-emerald-200/70 rounded-xl p-6 text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <i className="ri-checkbox-circle-fill text-3xl text-emerald-600"></i>
            </div>
            <p className="text-lg font-semibold text-foreground-950">İçe Aktarma Tamamlandı!</p>
            <p className="text-sm text-foreground-500">
              {result.imported} satış başarıyla eklendi.
              {result.skipped > 0 && ` ${result.skipped} satır zaten mevcut olduğu için atlandı.`}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 text-center">
              <p className="text-xs text-foreground-400 mb-1">Toplam Satır</p>
              <p className="text-xl font-bold text-foreground-950">{result.total}</p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 text-center">
              <p className="text-xs text-foreground-400 mb-1">Eklendi</p>
              <p className="text-xl font-bold text-emerald-600">{result.imported}</p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 text-center">
              <p className="text-xs text-foreground-400 mb-1">Atlandı (Zaten Var)</p>
              <p className="text-xl font-bold text-amber-600">{result.skipped}</p>
            </div>
          </div>

          {result.errors.length > 0 && (
            <div className="bg-accent-50 border border-accent-200/70 rounded-xl p-4">
              <p className="text-sm font-medium text-accent-700 mb-2">
                <i className="ri-error-warning-line mr-1"></i>
                Hatalar ({result.errors.length})
              </p>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {result.errors.map((err, i) => (
                  <p key={i} className="text-xs text-accent-600">{err}</p>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-center">
            <button
              onClick={() => { setStep("upload"); setParsedRows([]); setPreviewRows([]); setFile(null); setResult(null); setError(""); }}
              className="px-4 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-restart-line mr-1"></i>
              Yeni İçe Aktarma
            </button>
            <Link
              to="/reports/restaurant"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-list-3-line"></i>
              Satış Raporlarına Git
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}